import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toast } from 'react-hot-toast';

interface Channel {
  id: string;
  name: string;
  logo: string;
  active: boolean;
  webhookUrl: string;
}

interface GlobalConfig {
  [channelId: string]: {
    webhookUrl: string;
  };
}

interface CompanyConfig {
  [channelId: string]: {
    active: boolean;
  };
}

export default function ChannelActivations() {
  const { currentUser } = useAuth();
  const [channels, setChannels] = useState<Channel[]>([
    { id: 'amazon', name: 'Amazon', logo: '/images/amazon.png', active: false, webhookUrl: '' },
    { id: 'fnac', name: 'Fnac', logo: '/images/fnac.png', active: false, webhookUrl: '' },
    { id: 'worten', name: 'Worten', logo: '/images/worten.png', active: false, webhookUrl: '' },
    { id: 'kuantokusta', name: 'KuantoKusta', logo: '/images/kuantokusta.png', active: false, webhookUrl: '' },
  ]);
  const [loading, setLoading] = useState('');

  useEffect(() => {
    if (currentUser?.companyId) {
      loadChannelStates();
    }
  }, [currentUser?.companyId]);

  const loadChannelStates = async () => {
    if (!currentUser?.companyId) return;

    try {
      // Carregar configurações globais
      const globalConfigDoc = await getDoc(doc(db, 'config', 'multicanal'));
      const globalConfig: GlobalConfig = globalConfigDoc.exists() ? globalConfigDoc.data() : {};

      // Carregar estado de ativação da empresa
      const companyConfigDoc = await getDoc(doc(db, 'multicanal', currentUser.companyId));
      const companyConfig: CompanyConfig = companyConfigDoc.exists() ? companyConfigDoc.data() : {};

      setChannels(prev => prev.map(channel => ({
        ...channel,
        active: companyConfig[channel.id]?.active || false,
        webhookUrl: globalConfig[channel.id]?.webhookUrl || '',
      })));
    } catch (error) {
      console.error('Error loading channel states:', error);
      toast.error('Erro ao carregar estado dos canais');
    }
  };

  const handleToggleChannel = async (channelId: string) => {
    if (!currentUser?.companyId) return;

    const channel = channels.find(c => c.id === channelId);
    if (!channel?.webhookUrl && !channel?.active) {
      toast.error(`Configure a URL de funcionamento ${channelId === 'kuantokusta' ? 'do' : 'da'} ${channelId} primeiro`);
      return;
    }

    setLoading(channelId);
    try {
      const newActive = !channel.active;

      // Atualizar apenas o estado de ativação para a empresa
      await setDoc(doc(db, 'multicanal', currentUser.companyId), {
        [channelId]: { active: newActive }
      }, { merge: true });

      setChannels(prev => prev.map(c => 
        c.id === channelId ? { ...c, active: newActive } : c
      ));

      toast.success(`Canal ${channelId} ${newActive ? 'ativado' : 'desativado'} com sucesso`);
    } catch (error) {
      console.error(`Error toggling ${channelId}:`, error);
      toast.error(`Erro ao ${channel.active ? 'desativar' : 'ativar'} o canal ${channelId}`);
    }
    setLoading('');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <h2 className="text-3xl font-bold tracking-tight">Ativação de Canais</h2>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {channels.map((channel) => (
              <Card key={channel.id} className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <img
                    src={channel.logo}
                    alt={channel.name}
                    className="h-8 w-auto"
                  />
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={channel.active}
                      onCheckedChange={() => handleToggleChannel(channel.id)}
                      disabled={loading === channel.id || (!channel.active && !channel.webhookUrl)}
                    />
                  </div>
                </div>
                <div className="flex flex-col space-y-1">
                  <div className={`text-sm font-medium ${channel.active ? 'text-green-600' : 'text-gray-500'}`}>
                    {channel.active ? 'ATIVO' : 'DESATIVO'}
                  </div>
                  {!channel.webhookUrl && !channel.active && (
                    <div className="text-xs text-amber-600">
                      Brevemente
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
